import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalPartners, useHealthcareProviders } from "@/hooks/use-queries";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Building2, Plus, TrendingUp, Package, DollarSign, Download, Clock } from "lucide-react";
import { PageAbout } from "@/components/page-about";
import { exportPartnersToCsv } from "./hospital-partners/shared";
import { PartnerCard } from "./hospital-partners/partner-card";
import { PartnerFormDialog } from "./hospital-partners/partner-form";
import { PartnerDashboard } from "./hospital-partners/partner-dashboard";

export default function HospitalPartnersPage() {
  usePageTitle("Hospital Partnerships", "HealthWallet partners with Twin Cities hospitals to offer flexible payment plans and community-funded care.");
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const { data: partners, isLoading: partnersLoading } = useHospitalPartners();
  
  const { data: providers } = useHealthcareProviders();
  
  if (selectedPartnerId && providers) {
    return (
      <PartnerDashboard 
        partnerId={selectedPartnerId} 
        onBack={() => setSelectedPartnerId(null)}
        providers={providers}
      />
    );
  }
  
  const activePartners = partners?.filter(p => p.status === "active") || [];
  const pendingPartners = partners?.filter(p => p.status === "pending") || [];
  const totalRevenue = partners?.reduce((sum, p) => sum + (p.totalRevenue || 0), 0) || 0;
  const totalOrders = partners?.reduce((sum, p) => sum + (p.totalOrders || 0), 0) || 0;
  
  const handleExport = () => {
    if (partners && partners.length > 0) {
      exportPartnersToCsv(partners, t);
      toast({ title: t('hospitalPartners.exportSuccess', 'Export Complete'), description: t('hospitalPartners.exportSuccessDesc', 'Partner data has been exported to CSV.') });
    }
  };
  
  return (
    <div className="space-y-6">
      <PageAbout
        title="Hospital Partners"
        description="Manage B2B hospital partnerships for wallet-based payment integration. Hospital partners can access pre-funded patient wallets to receive payment for services, improving cash flow and reducing bad debt."
        features={[
          "Create and manage hospital partner accounts with custom terms",
          "Track order volume, deliveries, and revenue per partner",
          "View partnership status and contract details at a glance",
          "Access detailed partner portals with ROI and revenue cycle metrics",
          "Export partner data to CSV for external reporting"
        ]}
        tips={[
          "Active partners appear at the top for quick access",
          "Click on a partner card to view their full dashboard",
          "Use the Revenue Cycle tab in partner portals to see financial impact"
        ]}
      />
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-8 w-8" />
            {t('hospitalPartners.title', 'Hospital Partners')}
          </h1>
          <p className="text-muted-foreground">
            {t('hospitalPartners.description', 'Manage hospital partnerships and track performance metrics')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {partners && partners.length > 0 && (
            <Button variant="outline" onClick={handleExport} data-testid="button-export">
              <Download className="h-4 w-4 mr-2" />
              {t('hospitalPartners.export', 'Export CSV')}
            </Button>
          )}
          {user?.role === "admin" && providers && (
            <Button onClick={() => setCreateDialogOpen(true)} data-testid="button-create-partner">
              <Plus className="h-4 w-4 mr-2" />
              {t('hospitalPartners.addPartner', 'Add Partner')}
            </Button>
          )}
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.totalPartners', 'Total Partners')}</p>
                <p className="text-2xl font-bold">{partners?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.activePartners', 'Active')}</p>
                <p className="text-2xl font-bold">{activePartners.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.totalOrders', 'Total Orders')}</p>
                <p className="text-2xl font-bold">{totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.totalRevenue', 'Total Revenue')}</p>
                <p className="text-2xl font-bold">${(totalRevenue / 100).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {partnersLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('common.loading', 'Loading...')}</div>
        </div>
      ) : partners?.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">{t('hospitalPartners.noPartners', 'No Hospital Partners')}</h3>
            <p className="text-muted-foreground text-center max-w-sm mt-2">
              {t('hospitalPartners.noPartnersDesc', 'Get started by adding your first hospital partner to the network.')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {pendingPartners.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t('hospitalPartners.pendingPartners', 'Pending Approval')} ({pendingPartners.length})
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {pendingPartners.map(partner => (
                  <PartnerCard 
                    key={partner.id} 
                    partner={partner} 
                    onView={() => setSelectedPartnerId(partner.id)} 
                  />
                ))}
              </div>
            </div>
          )}
          
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {t('hospitalPartners.allPartners', 'All Partners')} ({partners?.filter(p => p.status !== "pending").length || 0})
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {partners?.filter(p => p.status !== "pending").map(partner => (
                <PartnerCard 
                  key={partner.id} 
                  partner={partner} 
                  onView={() => setSelectedPartnerId(partner.id)} 
                />
              ))}
            </div>
          </div>
        </div>
      )}
      
      {createDialogOpen && providers && (
        <PartnerFormDialog
          providers={providers}
          existingPartners={partners || []}
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}
